import toolAstrologer from "@/assets/svg/landing/tool-astrologer.svg"
import toolHoroscopes from "@/assets/svg/landing/tool-horoscopes.svg"
import toolPalmistry from "@/assets/svg/landing/tool-palmistry.svg"
import toolCompatibility from "@/assets/svg/landing/tool-compatibility.svg"
import toolTarot from "@/assets/svg/landing/tool-tarot.svg"
import toolMoon from "@/assets/svg/landing/tool-moon.svg"

import advisor1 from "@/assets/img/landing/vanessa.webp"
import advisor2 from "@/assets/img/landing/meera.webp"
import advisor3 from "@/assets/img/landing/dylan.webp"
import advisor4 from "@/assets/img/landing/sanjay.webp"

import reviewer1 from "@/assets/img/landing/reviewer-1.webp"
import reviewer2 from "@/assets/img/landing/reviewer-2.webp"
import reviewer3 from "@/assets/img/landing/reviewer-3.webp"

/** Support inbox used for refund requests (mirrors the in-app Help & Legal menu). */
export const SUPPORT_EMAIL = "support@astrology.clinic"
export const REFUND_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=Refund%20request`

/** A guidance "tool" card in the Tools section. */
export interface ToolItem {
  icon: string
  title: string
  subtitle: string
  cta: string
}

export const TOOLS: ToolItem[] = [
  {
    icon: toolAstrologer,
    title: "1-on-1 Astrologer Guidance",
    subtitle:
      "Talk directly with an astrologer and get personal guidance about love, timing, relationships, and your next decisions",
    cta: "Explore Astrologers",
  },
  {
    icon: toolHoroscopes,
    title: "Personalized Horoscopes",
    subtitle:
      "Get daily insights about love, energy, focus, and timing, based on your zodiac profile",
    cta: "Exlpore Horoscopes",
  },
  {
    icon: toolPalmistry,
    title: "Palmistry Insights",
    subtitle:
      "Discover what your palm lines may reveal about your personality, strengths, emotional patterns, and future path",
    cta: "Exlpore Palm Reading",
  },
  {
    icon: toolCompatibility,
    title: "Love & Compatibility",
    subtitle:
      "Explore emotional fit, chemistry, trust signals, hidden tension, and long-term relationship potential",
    cta: "Exlpore Compatibility",
  },
  {
    icon: toolTarot,
    title: "Tarot & Rune Guidance",
    subtitle:
      "Ask what’s on your mind and receive symbolic guidance for choices, doubts, and important moments",
    cta: "Exlpore Tarot & Runes",
  },
  {
    icon: toolMoon,
    title: "Moon & Biorhythm Cycles",
    subtitle:
      "Track lunar phases and personal energy cycles to understand your stronger and weaker days",
    cta: "Exlpore Moon & Biorhythm",
  },
]

/** An advisor in the "Ask an Astrologer Directly" section. */
export interface AdvisorItem {
  avatar: string
  name: string
  bio: string
  message: string
  cta: string
}

export const ADVISORS: AdvisorItem[] = [
  {
    avatar: advisor1,
    name: "Vanessa Cole",
    bio: "Emotional astrology · 13 y. of practice",
    message:
      "Your feelings may be pointing to something deeper. Let’s look at love, timing, and the choice in front of you",
    cta: "Ask Vanessa",
  },
  {
    avatar: advisor2,
    name: "Meera Rajan",
    bio: "Astrologer · 10 y. of practice",
    message:
      "Today’s cosmic signals can reveal where your energy is moving and what deserves your attention now",
    cta: "Ask Meera",
  },
  {
    avatar: advisor3,
    name: "Dylan Brooks",
    bio: "Karmic astrologer · 9 y. of practice",
    message:
      "Some patterns return for a reason. I’ll help you see what lesson may be showing up in your life right now",
    cta: "Ask Dylan",
  },
  {
    avatar: advisor4,
    name: "Sanjay Kapoor",
    bio: "Tarot · 15 y. of practice",
    message:
      "I can help you look at your question through tarot and reveal what the cards may be pointing toward",
    cta: "Ask Sanjay",
  },
]

/** A check-listed advantage in the "Everything You Need" section. */
export interface AdvantageItem {
  title: string
  subtitle: string
}

export const ADVANTAGES: AdvantageItem[] = [
  {
    title: "Daily insights in one place",
    subtitle: "Horoscopes, moon phases, biorhythms, and personal energy updates",
  },
  {
    title: "Deep personal reports",
    subtitle: "Explore palmistry, compatibility, soulmate, astrology, and astro map readings",
  },
  {
    title: "Guidance for real questions",
    subtitle: "Use tarot, runes, and astrologers when you need clarity",
  },
  {
    title: "Love and timing support",
    subtitle:
      "Understand relationship patterns, emotional signals, and stronger days for decisions",
  },
  {
    title: "Always available",
    subtitle: "Open your readings anytime and continue exploring your personal profile",
  },
]

/** A user review in the "What users say" section. */
export interface ReviewItem {
  avatar: string
  name: string
  rating: string
  text: string
}

export const REVIEWS: ReviewItem[] = [
  {
    avatar: reviewer1,
    name: "Maya Collins",
    rating: "5",
    text: "I started with a simple daily reading, but ended up exploring horoscopes, palmistry, tarot, and moon phases. Everything felt connected and surprisingly personal",
  },
  {
    avatar: reviewer2,
    name: "Sophie Grant",
    rating: "4.9",
    text: "The app helped me understand my timing, emotions, and relationship patterns better. I like having daily guidance and deeper reports in one place",
  },
  {
    avatar: reviewer3,
    name: "Daniel Brooks",
    rating: "5",
    text: "It wasn’t just another horoscope app. Live Palmistry gave me practical insights about love, decisions, and personal patterns I hadn’t noticed before",
  },
]

/** A numbered step in the "How Live Palmistry Works" section. */
export interface StepItem {
  title: string
  subtitle: string
}

export const STEPS: StepItem[] = [
  {
    title: "Complete the personal quiz",
    subtitle:
      "Answer a few quick questions so we can understand your signs, goals, and current situation",
  },
  {
    title: "Build your insight profile",
    subtitle: "We connect your answers with astrology, palmistry, cycles, and spiritual tools",
  },
  {
    title: "Unlock your personal readings",
    subtitle: "Get access to reports, daily guidance, tarot, runes, moon phases, and biorhythms",
  },
  {
    title: "Access your profile",
    subtitle:
      "Sign in to your account anytime to view your personalized readings and detailed astrology forecast",
  },
]

/** A headline stat in the Outro section. */
export interface StatItem {
  value: string
  label: string
}

export const STATS: StatItem[] = [
  { value: "94%", label: "Users recommend Live Palmistry" },
  { value: "2M+", label: "Personal readings revealed" },
  { value: "4.8/5", label: "Average rating from users" },
  { value: "24/7", label: "Guidance available anytime" },
]
